/**
 * Format an ISO date string for display using the user's locale
 *
 * @param {string|Date|null} dateString - ISO date string or Date object
 * @returns {string} - Formatted date string according to user's locale, or '-' for null/invalid/epoch dates
 */
export const formatISODate = (dateString) => {
  if (!dateString) return '-';

  const date = new Date(dateString);

  // Check for invalid date (NaN) or epoch date (1970-01-01)
  if (Number.isNaN(date.getTime()) || date.getTime() === 0) {
    return '-';
  }

  // Use undefined to let Intl.DateTimeFormat use the user's default locale
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(date);
};

/**
 * Convert local time (HH:MM) to UTC time (HH:MM)
 * Used when saving schedule times to the server (server stores UTC)
 *
 * @param {string} localTime - Time in HH:MM format (user's local timezone)
 * @returns {string} - Time in HH:MM format (UTC)
 *
 * @example
 * // If user is in EST (UTC-5) and enters 09:00
 * localTimeToUtc('09:00') // Returns '14:00'
 */
export const localTimeToUtc = (localTime) => {
  if (!localTime) return localTime;
  const [hours, minutes] = localTime.split(':').map(Number);
  const now = new Date();
  now.setHours(hours, minutes, 0, 0);
  const utcHours = now.getUTCHours().toString().padStart(2, '0');
  const utcMinutes = now.getUTCMinutes().toString().padStart(2, '0');
  return `${utcHours}:${utcMinutes}`;
};

/**
 * Convert UTC time (HH:MM) to local time (HH:MM)
 * Used when displaying schedule times from the server (server stores UTC)
 *
 * @param {string} utcTime - Time in HH:MM format (UTC)
 * @returns {string} - Time in HH:MM format (user's local timezone)
 *
 * @example
 * // If user is in EST (UTC-5) and server has 14:00
 * utcTimeToLocal('14:00') // Returns '09:00'
 */
export const utcTimeToLocal = (utcTime) => {
  if (!utcTime) return utcTime;
  const [hours, minutes] = utcTime.split(':').map(Number);
  const now = new Date();
  now.setUTCHours(hours, minutes, 0, 0);
  const localHours = now.getHours().toString().padStart(2, '0');
  const localMinutes = now.getMinutes().toString().padStart(2, '0');
  return `${localHours}:${localMinutes}`;
};

/**
 * Get the user's current timezone name
 *
 * @returns {string} - IANA timezone name (e.g., 'America/New_York')
 */
export const getUserTimezone = () => Intl.DateTimeFormat().resolvedOptions().timeZone;

/**
 * Get the user's timezone offset in hours
 *
 * @returns {number} - Offset in hours (e.g., -5 for EST)
 */
export const getTimezoneOffset = () => -new Date().getTimezoneOffset() / 60;

/**
 * Parse a GMT offset string into hours
 * @param {string|null|undefined} offset - GMT offset string (e.g., '+5', '-5', '+5:30')
 * @returns {number|null} - Offset in hours, or null if invalid
 */
export const parseGmtOffset = (offset) => {
  if (!offset) return null;

  const match = String(offset).trim().match(/^([+-])?(\d{1,2})(?::(\d{2}))?$/);
  if (!match) return null;

  const sign = match[1] === '-' ? -1 : 1;
  const hours = parseInt(match[2], 10);
  const minutes = match[3] ? parseInt(match[3], 10) : 0;

  return sign * (hours + minutes / 60);
};

/**
 * Check if a value looks like a Unix timestamp (seconds since epoch)
 * Valid range: 2000-01-01 to 2100-01-01 (matches API implementation)
 * @param {unknown} value - Value to check
 * @returns {boolean} - True if value is a valid Unix timestamp
 */
export const isUnixTimestamp = (value) =>
  typeof value === 'number' && Number.isFinite(value) && value > 946684800 && value < 4102444800;

/**
 * Check if a column name suggests it contains a duration value (in seconds).
 * Duration columns are formatted as human-readable time spans (e.g. "2h 15m 30s")
 * rather than plain integers.
 * @param {string} columnName - Column name to check
 * @returns {boolean} - True if column likely contains duration values in seconds
 */
export const isDurationColumn = (columnName) => {
  if (!columnName) return false;
  const lower = columnName.toLowerCase();
  return (
    lower === 'duration' ||
    lower === 'duration_seconds' ||
    lower === 'downtime' ||
    lower === 'uptime' ||
    lower === 'elapsed' ||
    lower === 'response_time' ||
    lower.endsWith('_duration') ||
    lower.endsWith(' duration') ||
    lower.endsWith('_elapsed') ||
    lower.endsWith(' elapsed') ||
    lower.endsWith('_downtime') ||
    lower.endsWith(' downtime') ||
    lower.endsWith('_uptime') ||
    lower.endsWith(' uptime')
  );
};

/**
 * Format a duration in seconds to a human-readable string.
 * - Values under 60s: "45s"
 * - Values under 1h: "12m 30s"
 * - Values under 24h: "2h 15m 30s"
 * - Values 24h+: "3d 4h 15m"
 *
 * @param {number} totalSeconds - Duration in seconds
 * @returns {string} - Human-readable duration string
 */
export const formatDuration = (totalSeconds) => {
  if (typeof totalSeconds !== 'number' || !Number.isFinite(totalSeconds)) return '-';
  const abs = Math.abs(totalSeconds);
  if (abs === 0) return '0s';

  const days = Math.floor(abs / 86400);
  const hours = Math.floor((abs % 86400) / 3600);
  const minutes = Math.floor((abs % 3600) / 60);
  const seconds = Math.floor(abs % 60);

  if (days > 0) {
    const parts = [`${days}d`];
    if (hours > 0) parts.push(`${hours}h`);
    if (minutes > 0) parts.push(`${minutes}m`);
    return parts.join(' ');
  }

  if (hours > 0) {
    const parts = [`${hours}h`];
    if (minutes > 0) parts.push(`${minutes}m`);
    if (seconds > 0) parts.push(`${seconds}s`);
    return parts.join(' ');
  }

  if (minutes > 0) {
    const parts = [`${minutes}m`];
    if (seconds > 0) parts.push(`${seconds}s`);
    return parts.join(' ');
  }

  return `${seconds}s`;
};

/**
 * Check if a column name suggests it contains timestamp data
 * Uses explicit string checks to avoid false positives (matches API implementation)
 * @param {string} columnName - Column name to check
 * @returns {boolean} - True if column likely contains timestamps
 */
export const isTimestampColumn = (columnName) => {
  if (!columnName) return false;
  const lower = columnName.toLowerCase();
  return (
    lower.includes('timestamp') ||
    lower.includes('_time') ||
    lower.endsWith(' time') ||
    lower === 'time' ||
    lower.includes('_date') ||
    lower.endsWith(' date') ||
    lower === 'date' ||
    lower.includes('created_at') ||
    lower.includes('updated_at') ||
    lower.includes('last_check') ||
    lower.includes('last_state_change') ||
    lower.includes('last event') ||
    lower === 'executed' ||
    lower === 'remediated'
  );
};

/**
 * Format a Unix timestamp with a GMT offset
 * Used for "host timezone" display where we have an offset like "+5" or "-8"
 *
 * @param {number} timestamp - Unix timestamp in seconds
 * @param {string} gmtOffset - GMT offset string (e.g., '+5', '-5', '+5:30')
 * @returns {string} - Formatted date string with offset indicator
 */
export const formatTimestampWithGmtOffset = (timestamp, gmtOffset) => {
  if (!isUnixTimestamp(timestamp)) return String(timestamp);

  const offsetHours = parseGmtOffset(gmtOffset);
  if (offsetHours === null) {
    // Invalid offset, fall back to local timezone
    return new Date(timestamp * 1000).toLocaleString();
  }

  const date = new Date(timestamp * 1000);
  const offsetMs = offsetHours * 60 * 60 * 1000;

  // Adjust by offset (treating original timestamp as UTC)
  const adjustedDate = new Date(date.getTime() + offsetMs);

  // Format using UTC methods to avoid browser timezone interference
  const year = adjustedDate.getUTCFullYear();
  const month = adjustedDate.getUTCMonth() + 1;
  const day = adjustedDate.getUTCDate();
  const hour = adjustedDate.getUTCHours();
  const minute = adjustedDate.getUTCMinutes().toString().padStart(2, '0');
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const hour12 = hour % 12 || 12;

  // Format offset for display (handle half-hour offsets like +5:30 or -5:30)
  const offsetSign = offsetHours >= 0 ? '+' : '-';
  const absHours = Math.abs(offsetHours);
  const offsetDisplay = Number.isInteger(absHours)
    ? `GMT${offsetSign}${absHours}`
    : `GMT${offsetSign}${Math.floor(absHours)}:${Math.round((absHours % 1) * 60)
        .toString()
        .padStart(2, '0')}`;

  return `${month}/${day}/${year}, ${hour12}:${minute} ${ampm} (${offsetDisplay})`;
};

/**
 * Format a Unix timestamp for display
 * Supports both local timezone and host timezone (GMT offset)
 *
 * @param {number} timestamp - Unix timestamp in seconds
 * @param {Object} options - Formatting options
 * @param {string} [options.timezoneType] - 'local' or 'host'
 * @param {string} [options.gmtOffset] - GMT offset string for host timezone (e.g., '+5')
 * @returns {string} - Formatted date string
 */
export const formatTimestamp = (timestamp, options = {}) => {
  if (!isUnixTimestamp(timestamp)) return String(timestamp);

  const { timezoneType, gmtOffset } = options;

  // For 'host' timezone with valid offset, use GMT offset formatting
  if (timezoneType === 'host' && gmtOffset) {
    return formatTimestampWithGmtOffset(timestamp, gmtOffset);
  }

  // Default: use browser's local timezone
  return new Date(timestamp * 1000).toLocaleString();
};

/**
 * Format a cell value with timezone awareness
 * Generic utility for formatting table cell values, handling timestamps specially
 *
 * @param {unknown} value - The cell value
 * @param {string} columnName - The column name (used to detect timestamp columns)
 * @param {Object} options - Formatting options
 * @param {string} [options.timezoneType] - 'local' or 'host'
 * @param {string} [options.gmtOffset] - GMT offset for host timezone
 * @returns {string} - Formatted value
 */
export const formatCellValue = (value, columnName, options = {}) => {
  if (value === null || value === undefined || value === '') return '-';

  const isTimestampCol = isTimestampColumn(columnName);

  // Unix timestamp in a timestamp column
  if (isTimestampCol && isUnixTimestamp(value)) {
    return formatTimestamp(value, options);
  }

  // Duration column (seconds) → human-readable time span
  if (isDurationColumn(columnName) && typeof value === 'number') {
    return formatDuration(value);
  }

  // Number formatting
  if (typeof value === 'number') return value.toLocaleString();

  // Boolean formatting
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';

  // String that looks like a date in a timestamp column
  if (isTimestampCol && typeof value === 'string') {
    const date = new Date(value);
    if (!Number.isNaN(date.getTime())) {
      return date.toLocaleString();
    }
  }

  return String(value);
};
